'use strict'
/**
 * Extend Protection - Shopify integration script
 * @author Extend, Inc
 * @version 2025.11
 */

class ExtendProductOffer extends HTMLElement {
    // * @param {string | number} params.referenceId - Variant ID of the covered product
    // * @param {string} params.category - Category of covered product
    // * @param {number} params.price - Price of covered product

    /**
     * Required parameters to generate an offer
     * @typedef ProductOfferParams
     * @type {object}
     * @property {string | number} referenceId - Variant ID of covered product
     * @property {string} category - Category of covered product
     * @property {number} price - Price of covered product in cents
     */
    
    /**
     * @param {ProductOfferParams} params - Parameters for this product protection offer
     */
    constructor({ referenceId, category, price }) {
        super();

        /**
         * @type {ProductOfferParams}
         */
        this.params = {
            referenceId,
            category,
            price
        }
    }

    disconnectedCallback() {
        this.instance()?.destroy()
    }

    instance() {
        return Extend.buttons.instance(this);
    }

}

// ! ||--------------------------------------------------------------------------------||
// ! ||                                   PDP Offers                                   ||
// ! ||--------------------------------------------------------------------------------||

class ProductPageOffer extends ExtendProductOffer {
    constructor({ productForm, addToCartButton, params, quantity='[name="quantity"]' }) {
        if (typeof params === 'undefined' || !params) {
            if (typeof productForm == 'string') {
                throw new ExtendError('Cannot create Extend PDP Offer - params object required when only product form selector provided')
            }
            const referenceId = productForm.id.value;
            const price = meta.product.variants.find(variant => variant.id == referenceId).price;
            const category = meta.product.type;
            params = {
                referenceId, price, category
            }
        }
        super(params);
        this.config = getConfiguration('pdp');
        
        try {
            this.productForm = document.querySelector(productForm)
        } catch {
            this.productForm = productForm;
        }

        try {
            this.addToCartButton = this.productForm ? this.productForm.querySelector(addToCartButton) : document.querySelector(addToCartButton);
        } catch {
            this.addToCartButton = addToCartButton; 
        }

        this.quantitySelector = quantity
        this.variantPicker = document.querySelector('variant-picker')
    }
    
    connectedCallback() {
        try {
            this.verifyProductOffer();
        } catch (err) {
            console.error(err.message);
            return;
        }
        
        this.bindEvents();
        this.style.display = 'block';
        
        if (!this.config.enabled) this.style.display = 'none';
        // Renders Extend offer
        Extend.buttons.render(this, this.params);
    }

    bindEvents() {
        this.handleAddToCart = this.handleAddToCart.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        
        this.productForm.addEventListener('product:rerender', this.handleVariantChange, true);
        this.productForm.addEventListener('submit', this.handleAddToCart, true);
    }

    verifyProductOffer() {
        
        let elementsToCheck = [
            'this.productForm',
            'this.addToCartButton',
            'this.params',
            'this.params.referenceId',
            'this.params.price',
            'this.params.category'
        ]
        
        let missingElements = elementsToCheck.filter((value) => {
            try {
                return !eval(value);
            } catch {
                return true;
            }
        });
        
        if (missingElements.length) {
            let message = "The following value(s) are null or undefined:\n";
            missingElements.forEach((value) => {
                message += `\t${value}\n`;
            });
            throw new ExtendError(message);
        }
        
        // Checks if Extend lives in the window and the active currency is USD before showing Extend offers
        verifyExtend();
    }

    modalDone(targetElement, eventType) {
        // remove default click listener
        targetElement.removeEventListener(eventType, this.handleAddToCart, true);
        // click atc button
        targetElement.dispatchEvent(new Event(eventType))
        // add default click listener back
        targetElement.addEventListener(eventType, this.handleAddToCart, true);
        
        if (this.config.triggerCart) window.setTimeout(() => {
            document.dispatchEvent(new Event('extend:plan-add-to-cart'));
        }, 500)
    }
    
    handleAddToCart(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        const quantityEl = this.productForm.querySelector(this.quantitySelector);
        const quantity = ((quantityEl) => {
            if (quantityEl) {
                if (quantityEl.tagName == 'input') {
                    return quantityEl.value;
                } else {
                    return quantityEl.innerText;
                }
            }
            return 1;
        })(quantityEl);
        
        ExtendShopify.handleAddToCart(this, {
            quantity: quantity,
            modal: this.config.triggerModal,
            done: () => {
                this.modalDone(e.target, e.type)
            }
        });
    }

    handleVariantChange() {
        // Flicker prevention
        this.style.height = `${this.clientHeight}px`;
        
        this.params.referenceId = this.variantPicker.selectedVariant.id
        this.params.price = this.variantPicker.selectedVariant.price
        
        Extend.setActiveProduct(this, this.params);
    }

    static tagName = 'extend-product-offer'
}
if (!customElements.get(ProductPageOffer.tagName))
    customElements.define(ProductPageOffer.tagName, ProductPageOffer);

const initProductOffer = (() => {

    const selectors = {
        productForm: 'main form[is="product-form"]',
        addToCartButton: 'button[type="submit"]'
    }

    return function init() {
        try {
            if ((meta.page.pageType != 'product' || !meta.product))
                throw new ExtendError("Extend Product Offer - The current page is not a product page");
        } catch (err) {
            console.warn(err.message);
            return;
        }

        const productForm = document.querySelector(selectors.productForm);
        const addToCartButton = productForm ? productForm.querySelector(selectors.addToCartButton) : document.querySelector(selectors.addToCartButton);

        // Create offer and render it
        const extendOffer = new ProductPageOffer({ productForm, addToCartButton });
        
        productForm.before(extendOffer); // before, after, append, prepend
    }
})()

// ! ||--------------------------------------------------------------------------------||
// ! ||                                   Cart Offer                                   ||
// ! ||--------------------------------------------------------------------------------||
class CartProductOffer extends ExtendProductOffer {

    constructor({ referenceId, category, price, quantity}, type="main") {
        super({ referenceId, category, price });

        this.addToCart = {
            quantity
        }

        /** @type {string} */
        this.type = type;

        /** @type {ExtendCartIntegrationConfig} */
        this.config = getConfiguration('cart')
    }
    
    connectedCallback() {
        if (!this.canDisplayOffer()) {
            return;
        }
        this.render();
    }

    /**
     * Mutation Observer callback to detect when the cart offer is rendered so that the side cart
     * can be hidden when the offer modal is opened and shown again when the modal is closed
     */
    observeOfferRendered(mutations, observer) {
        for (let mutation of mutations) {
            if (mutation.addedNodes[0]?.tagName == 'IFRAME' && mutation.target.id.includes('extend-simple-offer')) {
                const cartOfferIframe = mutation.addedNodes[0].contentDocument
                observer.disconnect()
                observer.observe(cartOfferIframe, { subtree: true, childList: true })
            }

            if (mutation.addedNodes[0]?.tagName == 'BUTTON' && mutation.target.firstChild == mutation.addedNodes[0]) {
                mutation.addedNodes[0].addEventListener('click', () => {
                    document.querySelector('cart-drawer').hide()
                    observer.observe(document.body, { subtree: false, childList: true })
                })
                observer.disconnect()
            }

            if (mutation.removedNodes[0]?.id == 'extend-offers-modal-iframe') {
                document.querySelector('cart-drawer').show()
                observer.disconnect()
            }
        }
    }

    _asyncRender() {
        if (this.type == 'side') {
            new MutationObserver(this.observeOfferRendered.bind(this)).observe(this, { childList: true, subtree: true })
        }

        return new Promise((resolve) => {
            Extend.buttons.renderSimpleOffer(this, {
                ...this.params,
                onAddToCart: ({ plan, product }) => {
                    resolve({ plan, product });
                }
            })
        })
    }

    _asyncAddToCart(selections) {
        return new Promise((resolve, reject) => {
            ExtendShopify.addPlanToCart({
                ...selections,
                quantity: this.addToCart.quantity
            }, function(err) {
                if (err) {
                    reject(err)
                }

                resolve()
            })
        })
    }

    render() {
        this._asyncRender().then((selections) => {
            return this._asyncAddToCart(selections)
        }).then(() => {
            document.dispatchEvent(new Event('extend:plan-added'));
        }).catch((err) => {
            throw new ExtendError(err.message);
        })
    }

    canDisplayOffer() {
        return this.config.enabledTypes.includes(this.type);
    }

    paramsEqual({ referenceId, price, category, quantity }) {
        return this.params.render.referenceId === referenceId &&
            this.params.render.price === price &&
            this.params.render.category === category &&
            this.params.addToCart.quantity === quantity;
    }

    static tagName = 'extend-cart-offer';
}
if (!customElements.get(CartProductOffer.tagName))
    customElements.define(CartProductOffer.tagName, CartProductOffer);

// ! ||--------------------------------------------------------------------------------||
// ! ||                             Cart Integration Script                            ||
// ! ||--------------------------------------------------------------------------------||
const initCartOffer = (() => {

    let cart;
    let isInit = false;
    
    const {
        shipping,
        cart: cartConfig
    } = getConfiguration('cart', 'shipping');
    
    
    let selectors = {};
    let type;

    if (meta.page.pageType === 'cart') {
        type = 'main';

        if (cartConfig.enabled && cartConfig.enabledTypes.includes('main')) {
            selectors = {
                cartContainer: '.order-summary__body',
                lineItem: {
                    warranty: {
                        image: 'img',
                        options: '.list-disc li',
                        optionsContainer: '.list-disc',
                        quantityWrapper: 'line-item-quantity input',
                    },
                    row: 'tr',
                    title: '.line-item__info div:first-child a',
                    quantity: 'line-item-quantity input',
                    offerAnchor: '.line-item__info',
                }
            }
        }

        if (shipping.offer === 'cart') {
            selectors.shipping = {
                anchor: ''
            }
        }

        if (shipping.offer === 'checkout') {
            selectors.checkoutPlus = {
                checkoutButton: '',
                anchor: ''
            }
        }
    } else if (cartConfig.enabledTypes.includes('side')) {
        type = 'side';

        if (cartConfig.enabled) {
            selectors = {
               cartContainer: 'cart-drawer',
                lineItem: {
                    warranty: {
                        image: 'img',
                        options: '.list-disc li',
                        optionsContainer: '.list-disc',
                        quantityWrapper: 'line-item-quantity input',
                    },
                    row: 'line-item',
                    title: '.line-item__info div:first-child a',
                    quantity: 'line-item-quantity input',
                    offerAnchor: '.line-item__info',
                }
            }
        }

        if (shipping.offer === 'cart') {
            selectors.shipping = {
                anchor: ''
            }
        }

        if (shipping.offer === 'checkout') {
            selectors.checkoutPlus = {
                checkoutButton: '',
                anchor: ''
            }
        }
    }

    function createWarrantyOffer(lineItemElement, index) {
        let cartItem = cart.items[index];

        const referenceId = new URL(lineItemElement.querySelector(selectors.lineItem.title).href).searchParams.get("variant")
        if (!cartItem || cartItem.variant_id !== referenceId) {
            cartItem = cart.items.find((item) => item.variant_id === params.referenceId)
        }

        const params = {
            referenceId: cartItem.variant_id,
            quantity: cartItem.quantity,
            price: cartItem.price,
            category: cartItem.product_type
        }
        const warrantyAlreadyInCart = ExtendShopify.warrantyAlreadyInCart(params.referenceId, cart.items) || ExtendShopify.warrantyAlreadyInCart(params.referenceId.toString(), cart.items);
        
        let extendOffer = lineItemElement.querySelector(CartProductOffer.tagName);
        
        // If there's already an offer element, check that the existing parameters match what we are trying to create
        if (extendOffer) {
            // If they do not match, or a warranty is in cart, remove the offer
            if (!extendOffer.paramsEqual(params) ||
                warrantyAlreadyInCart
            ) {
                extendOffer.remove();
                // If they do match and a warranty is not in cart, do not create a new offer
            } else {
                return;
            }
        // If there is no offer element and a warranty is in cart, do not create a new offer
        } else if (warrantyAlreadyInCart) {
            return;
        }
        
        let anchor = lineItemElement.querySelector(selectors.lineItem.offerAnchor);
        
        if (!anchor) {
            throw new ExtendError(`Offer anchor element on line ${index} not found with selector ${selectors.lineItem.offerAnchor}`);
        }

        let newExtendOffer = new CartProductOffer(params, type);
        
        anchor.append(newExtendOffer); // append, prepend, after, before
    }
    
    function styleWarrantyLineItem(lineItemElement, index) {
        const title = lineItemElement.querySelector(selectors.lineItem.title);
        title.style.pointerEvents = 'none';
        title.innerText = 'Extend Protection Plan';
        
        const image = lineItemElement.querySelector(selectors.lineItem.warranty.image);
        
        if (image) {
            image.style.pointerEvents = 'none';
        } else {
            console.warn(`EXTEND: Warranty image element on line ${index} not found with selector ${selectors.lineItem.warranty.image}`)
        }
        
        const warrantyOptions = lineItemElement.querySelectorAll(selectors.lineItem.warranty.options);
        
        if (warrantyOptions?.length) {
            warrantyOptions.forEach(each => {
                const property = each.innerHTML.trim().toLowerCase();
                
                if (!property.startsWith('product') && !property.startsWith('term')) each.remove();
                else {
                    each.classList.add('extend-warranty-info');
                }
            });
        }
        
        if (selectors.lineItem.warranty.quantityWrapper?.length && lineItemElement.querySelector(selectors.lineItem.warranty.quantityWrapper)) {
            lineItemElement.querySelectorAll(selectors.lineItem.warranty.quantityWrapper).forEach((each) => {
                each.style.opacity = '75%';
                each.style.pointerEvents = 'none';
            });
        }
    }

    function formatLineItems() {
        let cartContainer = document.querySelector(selectors.cartContainer);
        
        if (!cartContainer) {
            throw new ExtendError(`Invalid cart container selector: ${selectors.cartContainer}`);
        }
        
        cartContainer.querySelectorAll(selectors.lineItem.row).forEach((lineItemElement, index) => {

            let titleElement = lineItemElement.querySelector(selectors.lineItem.title);
                        
            let lowerCaseTitle = titleElement.innerHTML.toLowerCase();

            if (!lowerCaseTitle || !lowerCaseTitle.length) {
                throw new ExtendError(`Line item title not found with selector: ${selectors.lineItem.title}`);
            }

            // Put removed links back
            const extendLineItems = JSON.parse(sessionStorage.getItem('extend.line-items'));
            const hrefToReplace = lineItemElement.querySelector('line-item-quantity a');
            const referenceId = new URL(titleElement.href).searchParams.get("variant")
            const originalHref = extendLineItems[referenceId]

            if (originalHref && hrefToReplace.href.includes('javascript')) {
                setTimeout(() => {
                    hrefToReplace.setAttribute('href', originalHref)
                    hrefToReplace.removeAttribute('onclick')
                }, 1000)
            }
            
            if (lowerCaseTitle.includes('extend protection')) {
                styleWarrantyLineItem(lineItemElement, index);
            } else {
                createWarrantyOffer(lineItemElement, index);
            }
        });
    }

    function normalizeItems() {
        return new Promise((resolve, reject) => {
            const callback = function(err, data) {
                if (err) {
                    reject(new ExtendError(err))
                }

                resolve(data)
            }

            if (shipping.enabled) {
                ExtendShopify.updateExtendLineItems({
                    balanceCart: cartConfig.balanceCart,
                    isSPSupported: shipping.enabled,
                    callback
                });
            } else {
                ExtendShopify.normalizeCart({
                    balance: cartConfig.balanceCart
                }, callback);
            }
        })
    }

    function refreshCart() {
        if (type === 'side') {
            document.dispatchEvent(new Event('cart:refresh'));
            setTimeout(init, 250);
        } else if (type === 'main') {
            window.location.reload();
        } else {
            window.location.reload();
        }
    }

    function verifyCartOffers() {
        verifyExtend();
        
        let error = '';
        if (typeof selectors == 'undefined' || !selectors || Object.keys(selectors).length === 0) error += `selectors is empty or undefined\n`;
        
        if (typeof type == 'undefined' || !type || type.length === 0) error += `Cart type is empty or not defined\n`;
        
        if (error.length > 0) {
            throw new ExtendError(`Cannot initialize cart offers due to following errors:\n${error}`);
        }
    }
    
    function initEventListeners() {
        document.addEventListener('extend:plan-added', refreshCart);
        
        document.addEventListener('extend:cart-refreshed', () => {
            setTimeout(() => {
                init()
            }, 250);
        })
        
        isInit = true;
    }
    
    function init() {
        verifyCartOffers();
        
        try {
            if (type !== 'main' && !cartConfig.enabledTypes.includes('side')) {
                throw new ExtendError('Cannot initialize cart offers - not a cart page');
            }
        } catch (err) {
            console.warn(err.message);
            return;
        }
        
        if (!isInit) initEventListeners();
        
        normalizeItems().then((data) => {
            cart = data?.cart;
            
            if (data?.updates) {
                refreshCart();
            } else {
                formatLineItems();
            }
        })
    }

    return init
})();

let extendLineItems = {}
// Preserve line item remove href before they are removed by other app
document.querySelectorAll('line-item-quantity a').forEach((each) => {
    const productId = new URL(each.href).searchParams.get('id')?.split(':')[0]
    if (productId) extendLineItems[productId] = each.href
})
sessionStorage.setItem(`extend.line-items`, JSON.stringify(extendLineItems))


// ! ||--------------------------------------------------------------------------------||
// ! ||                                      Utils                                     ||
// ! ||--------------------------------------------------------------------------------||
class ExtendError extends Error {
    /**
     * @param {Error | string} error The error that occurred
     */
    constructor(error) {
        if (typeof error === 'string') {
            super("EXTEND: " + error);
        } else if (error instanceof Error) {
            super("EXTEND: " + error.message)
        }
    }
}

function getConfiguration(...offers) {
    /** @type {ExtendGlobalIntegrationConfig} */
    const config = {
        default: {
            currency: 'USD',
        },
        aftermarket: true,
        pdp: {
            enabled: true,
            triggerModal: true,
            atc: false,
            triggerCart: false
        },
        cart: {
            enabled: true,
            enabledTypes: [
                'main',
                'side'
            ],
            balanceCart: true,
        },
        shipping: {
            enabled: false,
            offer: "cart"
        }
    }
    if (typeof offers !== 'undefined' && offers.length > 0) {

        if (offers.length === 1) {
            return config[offers[0]]
        }

        let configuration = {}
        offers.forEach(offer => {
            configuration[offer] = config[offer]
        });
        return configuration
    }

    return config;
}

function verifyExtend() {
    const propertiesToCheck = [
        'window.Extend',
        'window.ExtendShopify',
        'window.Shopify',
        'window.Shopify.currency.active'
    ];
    
    function checkPropertyExists(path) {
        return path.split('.').reduce((obj, prop) => obj && obj[prop], window) !== undefined;
    }
    
    let errors = propertiesToCheck.map(prop => {
        if (!checkPropertyExists(prop)) {
            return `${prop} is undefined`;
        }
    }).filter(Boolean)
    
    // Check for currency mismatch
    if (window.Shopify?.currency?.active !== getConfiguration('default').currency) {
        errors.push('Active currency does not match configured currency');
    }
    
    if (errors.length) {
        let message = "The following errors occurred:\n";
        errors.forEach((error) => {
            message += `\t${error}\n`;
        });
        throw new ExtendError(message);
    }
}

function initAftermarket() {
    try {
        verifyExtend();
        
        // Get params from URL
        const params = (new URL(document.location)).searchParams;
        
        // Check if leadToken or leadtoken is in the URL
        let leadToken = params.get('leadToken')
        ? params.get('leadToken')
        : params.get('leadtoken');
        
        if (leadToken) {
            Extend.aftermarketModal.open({
                leadToken: leadToken,
                onClose: function (plan, product, quantity) {
                    if (plan && product) {
                        ExtendShopify.addPlanToCart({
                            plan: plan,
                            product: product,
                            leadToken: leadToken,
                            quantity: quantity || 1
                        }, function () {
                            location = '/cart'
                        })
                    }
                }
            });
        }
    } catch (error) {
        throw new ExtendError( error.message);
    }
}

// Integration init
window.addEventListener('DOMContentLoaded', () => {
    try {
        const storeId = '7dda49e3-1b34-4029-bd45-aad4ac80c14f';
        const environment = 'production';
        
        window.Extend.config({ storeId, environment});
        
        const config = getConfiguration();
        
        if (config.aftermarket)
            initAftermarket();
        
        if (config.pdp.enabled || config.pdp.atc || config.pdp.triggerModal)
            initProductOffer();
                
        if ((config.cart.enabled && config.cart.enabledTypes.length) ||
            config.cart.shipping.cartOffer ||
            config.cart.shipping.checkoutPlus) {

            initCartOffer();
        }
        
    } catch (err) {
        if (sessionStorage.getItem('extend.debug') && err instanceof ExtendError)
            console.error(err);

        throw err
    }
});